<template>
  <ModalCreateEditUser
    v-show="modalInfoUser"
    :user="userSelected"
    :roles="roles"
    @close="closeInformationUserModal"
    @create="createUserTransaction"
    @edit="editUserTransaction"
  />
  <ModalConfirmation
    :message="confirmMessage"
    @accept="closeConfirmModalAsync(deleteUserTransaction)"
    @deny="closeConfirmModalAsync"
    v-show="showConfirmModal"
  />
  <div class="wrapper-users">
    <div class="above-table">
      <p>Usuarios</p>
      <StandardButton :buttonLabel="'Crear usuario'" @click="createNewUser" />
    </div>

    <table>
      <thead>
        <tr>
          <th>Código</th>
          <th>Nombre empleado</th>
          <th>Nombre de usuario</th>
          <th>Rol</th>
          <th>Precio base</th>
          <th>Editar</th>
          <th>Eliminar</th>
        </tr>
      </thead>
      <tbody>
        <tr v-for="(user, index) in users" :key="index">
          <td>{{ user.emplid }}</td>
          <td>{{ user.name }}</td>
          <td>{{ user.username }}</td>
          <td>{{ user.rol.roleName }}</td>
          <td>{{ user.base_price }}</td>
          <td>
            <TableButton @click="editUserSelected(user)" :type="'Edit'" />
          </td>
          <td>
            <TableButton @click="selectDeletedUser(user)" :type="'Delete'" />
          </td>
        </tr>
      </tbody>
    </table>
  </div>
</template>

<script lang="ts">
import { defineComponent, inject } from "vue";
import ModalCreateEditUser from "./components/ModalCreateEditUser.vue";
import ModalConfirmation from "@/components/commons/modals/ModalConfirmation.vue";
import { usersUse } from "./use/usersUse";
import TableButton from "@/components/commons/controls/TableButton.vue";
import StandardButton from "@/components/commons/controls/StandardButton.vue";
import { spinnerProcessKey, successProcessKey } from "@/main";
import { confirmCancelMessageManager } from "@/ux/utils/managers/confirmCancelMessageManager";
import { User } from "@/business/users/domain/user";

export default defineComponent({
  name: "UsersView",
  components: {
    ModalCreateEditUser,
    ModalConfirmation,
    TableButton,
    StandardButton,
  },

  setup(props, context) {
    const successProcess = inject(successProcessKey)!;
    const { showSuccessModal } = successProcess;

    const spinnerProcess = inject(spinnerProcessKey)!;
    const { offSpinnerState, onSpinnerState } = spinnerProcess;

    const {
      showConfirmModal,
      openConfirmModal,
      closeConfirmModalAsync,
      confirmMessage,
    } = confirmCancelMessageManager();

    const {
      modalInfoUser,
      closeInformationUserModal,
      users,
      userSelected,
      createNewUser,
      editUserSelected,
      createUserOperation,
      editUserOperation,
      deleteUserOperation,
      roles,
    } = usersUse();

    async function createUserTransaction(user: {
      name: string;
      userName: string;
      role: string;
      code: string;
      price: string;
      password: string;
      email: string;
    }) {
      try {
        await onSpinnerState();
        await createUserOperation(
          user.name,
          user.userName,
          user.role,
          user.code,
          user.price,
          user.password,
          user.email
        );
        await offSpinnerState();
        showSuccessModal("Usuario creado correctamente");
      } catch (exception) {
        await offSpinnerState();
        throw exception;
      }
    }

    async function editUserTransaction(user: {
      name: string;
      userName: string;
      role: string;
      code: string;
      price: string;
      password: string;
      email: string;
    }) {
      try {
        await onSpinnerState();
        await editUserOperation(
          user.name,
          user.userName,
          user.role,
          user.code,
          user.price,
          user.password,
          user.email
        );
        await offSpinnerState();
        showSuccessModal("Usuario editado correctamente");
      } catch (exception) {
        await offSpinnerState();
        throw exception;
      }
    }

    async function selectDeletedUser(user: User) {
      userSelected.value = user;
      openConfirmModal("¿Estás seguro de eliminar el usuario?");
    }

    async function deleteUserTransaction(): Promise<void> {
      try {
        await onSpinnerState();
        await deleteUserOperation();
        await offSpinnerState();
        showSuccessModal("El usuario ha sido eliminado correctamente.");
      } catch (exception) {
        await offSpinnerState();
        throw exception;
      }
    }

    return {
      modalInfoUser,
      closeInformationUserModal,
      users,
      userSelected,
      createNewUser,
      editUserSelected,
      createUserOperation,
      editUserOperation,
      createUserTransaction,
      editUserTransaction,
      deleteUserTransaction,
      selectDeletedUser,
      showConfirmModal,
      openConfirmModal,
      closeConfirmModalAsync,
      confirmMessage,
      roles,
    };
  },
});
</script>

<style scoped lang="scss">
@import "src/ux/users/styles/usersStyle";
</style>
